import { DASHBOARD_SHORT_NAME, SaleTerm, SubscriptionTypeShortNames } from 'constants/payments';

export const RESUBSCRIBE_ACTIONS = {
  Create: 'create',
  Resume: 'resume',
  Switch: 'switch',
} as const;

/**
 * Terms the resubscribe offer sells, cheapest first. Lifetime is deliberately absent: we never
 * discount lifetime plans, and a lifetime holder has nothing to reactivate.
 */
export const RESUBSCRIBE_TERMS: SaleTerm[] = [
  SaleTerm.Monthly,
  SaleTerm.Quarterly,
  SaleTerm.Annual,
];

/**
 * A switch is only offered toward a longer commitment, so a customer can trade up but never
 * downgrade into a credit balance from inside this flow.
 */
export const isLongerResubscribeTerm = (candidate: SaleTerm, current: SaleTerm) =>
  RESUBSCRIBE_TERMS.indexOf(candidate) > RESUBSCRIBE_TERMS.indexOf(current);

interface BaseResubscribeRequest {
  requestId: string;
}

export interface CreateResubscribeRequest extends BaseResubscribeRequest {
  action: typeof RESUBSCRIBE_ACTIONS.Create;
  duration: SaleTerm;
  subscriptionType: SubscriptionTypeShortNames;
}

export interface ResumeResubscribeRequest extends BaseResubscribeRequest {
  action: typeof RESUBSCRIBE_ACTIONS.Resume;
  subscriptionId: string;
}

export interface SwitchResubscribeRequest extends BaseResubscribeRequest {
  action: typeof RESUBSCRIBE_ACTIONS.Switch;
  duration: SaleTerm;
  /** Locks the displayed charge to the amount Stripe actually bills. */
  prorationDate: number;
  subscriptionId: string;
}

export type ResubscribeRequest =
  | CreateResubscribeRequest
  | ResumeResubscribeRequest
  | SwitchResubscribeRequest;

export interface ResubscribeProduct {
  /** Shown as the heading above this product's plans, and in the confirmation. */
  name: string;
  subscriptionType: SubscriptionTypeShortNames;
}

/**
 * The products the reactivation offer sells, in display order. **This is the only list to edit.**
 * The plan grid, the API's accepted-product allowlist, the trade-up options, and the confirmation
 * copy all derive from it, so they cannot fall out of step with each other.
 *
 * To offer a new plan: add an entry here, and add its price ids to the matching map in
 * `constants/payments.ts` so `getPriceIdForResubscribe` can resolve them. To stop offering one:
 * delete its entry. Nothing else needs to change.
 *
 * Technical Analysis (`alerts`) was removed 2026-08-18. It is being retired as a standalone plan,
 * and the Dashboard plan already includes it — so a lapsed or cancelling customer is pointed at the
 * plan that supersedes it rather than being sold one we are about to withdraw. Removing it here also
 * stops existing Technical Analysis customers being offered a longer commitment on it. They can
 * still keep the subscription they hold: that is retention, costs nothing today, and is unaffected
 * by this list.
 */
export const RESUBSCRIBE_PRODUCTS: ResubscribeProduct[] = [
  { name: 'Dashboard + Technical Analysis', subscriptionType: DASHBOARD_SHORT_NAME },
];

export const isResubscribeProduct = (
  subscriptionType: SubscriptionTypeShortNames | string,
): boolean => RESUBSCRIBE_PRODUCTS.some((product) => product.subscriptionType === subscriptionType);

export const getResubscribeProductName = (
  subscriptionType: SubscriptionTypeShortNames | string,
): string =>
  RESUBSCRIBE_PRODUCTS.find((product) => product.subscriptionType === subscriptionType)?.name ||
  'InsiderFinance';

/** Every sellable combination, derived so the grid and the server always agree on what exists. */
export const RESUBSCRIBE_PLAN_OPTIONS: Array<{
  duration: SaleTerm;
  subscriptionType: SubscriptionTypeShortNames;
}> = RESUBSCRIBE_PRODUCTS.flatMap((product) =>
  RESUBSCRIBE_TERMS.map((duration) => ({
    duration,
    subscriptionType: product.subscriptionType,
  })),
);

export interface ResubscribePlanPrice {
  /** Stripe product for deciding whether an attached coupon follows this plan. */
  targetProductId: string;
  currency: string;
  duration: SaleTerm;
  originalAmountCents: number;
  priceId: string;
  subscriptionType: SubscriptionTypeShortNames;
}

/** A longer term the customer can trade up to while reversing a scheduled cancellation. */
export interface ResubscribeUpgradeOption extends ResubscribePlanPrice {
  subscriptionId: string;
}

export interface ResubscribePreviewResponse {
  plans: ResubscribePlanPrice[];
  upgradeOptions: ResubscribeUpgradeOption[];
  success: true;
}

/**
 * Exact Stripe numbers for a trade-up, read from the upcoming invoice before anything is billed.
 * There is deliberately no predicted renewal date: changing the billing interval moves Stripe's
 * period, so the confirmed date is read back from the subscription after the change instead.
 */
export interface ResubscribeSwitchPreviewResponse {
  amountDueTodayCents: number;
  currency: string;
  /**
   * The headline rate, for copy only — never compute money from it.
   *
   * It is 30% when the offer applies, and otherwise the customer's own forever discount measured
   * against the plan they are on **today**. For a flat `amount_off` coupon those are different
   * numbers: $24 off is 32% of the $75 monthly plan and 3.6% of the $660 annual one, so this
   * percentage does not describe the plan being quoted. `renewalAmountCents` is the authoritative
   * figure, and `preservedAmountOffCents` is set whenever the discount cannot be stated as a
   * percentage of the new plan.
   */
  discountPercent: number;
  duration: SaleTerm;
  /**
   * Set only when the preserved discount is a flat amount rather than a percentage. Such a coupon
   * takes the same sum off any plan, so it cannot be described as a percentage of a plan the
   * customer is not on yet.
   */
  preservedAmountOffCents: number | null;
  prorationDate: number;
  /** Post-discount, because that is what the customer will be billed. */
  renewalAmountCents: number;
  subscriptionId: string;
  success: true;
}

export interface ResubscribeResponse {
  action: ResubscribeRequest['action'];
  alreadyCompleted?: boolean;
  duration?: string;
  /** Read from Stripe after the change, so the success screen states a confirmed date. */
  nextRenewalAt?: number;
  /**
   * The forever rate a removed cancellation kept, read from Stripe after the change: the
   * customer's own discount when it beat the offer, otherwise the offer. The success screen states
   * this rather than the rate the page loaded with, which support may have changed since.
   */
  keptDiscountPercent?: number;
  priceId: string;
  subscriptionId: string;
  subscriptionType?: string;
  success: true;
}
